import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  Typography,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Alert,
  AlertColor,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import PageTitle from "../../molecules/PageTitle";
import { useNavigate, useParams } from "react-router-dom";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import RefreshIcon from "@mui/icons-material/Refresh";
import { CustomContainer } from "../../../styles/styles";
import {
  ErrorWithRetry,
  LoadingSpinner,
} from "../../molecules/LoadingAndError";
import {
  emailErrorMessage,
  emailRegex,
  phoneNumberErrorMessage,
  phoneNumberRegex,
} from "../../../utils/ValidationHelper";
import RequiredLabel from "../../atoms/RequiredLabel";
import ConfirmDialog from "../../molecules/ConfirmDialog";

const OwnerEditPage: React.FC = () => {
  const { autoOwnerId } = useParams();

  const [id, setId] = useState("");
  const [password, setPassword] = useState("");
  const [subscribeNumber, setSubscribeNumber] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [departmentName, setDepartmentName] = useState("");
  const [representativeName, setRepresentativeName] = useState("");
  const [inChargeName, setInChargeName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [memo, setMemo] = useState("");
  const [isEnabled, setIsEnabled] = useState("");

  // アラート
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    // window.location.reload();//下部アラートが消えた後に画面リロード
  };

  // 確認ダイアログ
  const [ownerDeletedConfirm, setOwnerDeletedConfirm] = React.useState(false);
  const [ownerDeleteConfirm, setOwnerDeleteConfirm] = React.useState(false);
  const [passwordUpdateConfirm, setPasswordUpdateConfirm] =
    React.useState(false);

  const CHARACTER_LIMIT = 50; // 文字数制限を定義
  const CHARACTER_LIMIT_SUBSCRIBE_NUMBER = 10;

  const [isTouched, setIsTouched] = useState({
    subscribeNumber: false,
    ownerName: false,
    sdcServiceName: false,
    departmentName: false,
    representativeName: false,
    inChargeName: false,
    phoneNumber: false,
    email: false,
    memo: false,
    isEnabled: false,
  });

  const [validationErrors, setValidationErrors] = useState({
    subscribeNumber: "",
    ownerName: "",
    departmentName: "",
    representativeName: "",
    inChargeName: "",
    phoneNumber: "",
    email: "",
    memo: "",
    isEnabled: "",
  });

  // 入力が変更されたときのハンドラ
  const handleBlur = (field: string) => {
    setIsTouched({
      ...isTouched,
      [field]: true,
    });
  };

  useEffect(() => {
    const errors = {
      ownerName:
        isTouched.ownerName &&
        ownerName !== "" &&
        ownerName.length > CHARACTER_LIMIT
          ? `名称・氏名は${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      subscribeNumber:
        isTouched.subscribeNumber &&
        subscribeNumber !== "" &&
        subscribeNumber.length > CHARACTER_LIMIT_SUBSCRIBE_NUMBER
          ? `契約者番号は${CHARACTER_LIMIT_SUBSCRIBE_NUMBER}桁以内で入力してください。`
          : "",
      departmentName:
        isTouched.departmentName &&
        departmentName !== "" &&
        departmentName.length > CHARACTER_LIMIT
          ? `会社名(部署名)は${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      representativeName:
        isTouched.representativeName &&
        representativeName !== "" &&
        representativeName.length > CHARACTER_LIMIT
          ? `代表者名は${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      inChargeName:
        isTouched.inChargeName &&
        inChargeName !== "" &&
        inChargeName.length > CHARACTER_LIMIT
          ? `担当者名は${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      phoneNumber:
        isTouched.phoneNumber &&
        phoneNumber &&
        !phoneNumber.match(phoneNumberRegex)
          ? phoneNumberErrorMessage
          : "",
      email:
        isTouched.email && email && !emailRegex.test(email)
          ? emailErrorMessage
          : "",
      memo:
        isTouched.memo && memo !== "" && memo.length > CHARACTER_LIMIT
          ? `メモは${CHARACTER_LIMIT}文字以内で入力してください。`
          : "",
      isEnabled:
        isTouched.isEnabled && !isEnabled ? "状態を選択してください" : "",
    };

    setValidationErrors(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTouched,
    subscribeNumber,
    ownerName,
    departmentName,
    representativeName,
    inChargeName,
    phoneNumber,
    email,
    memo,
  ]);

  const fetchData = async () => {
    setIsLoading(true);
    setHasError(false);
    // JWTトークンを取得します
    try {
      const jwtToken = await getJwtTokenFromIdToken();
      // call showOwner API
      const url = process.env.REACT_APP_API_ENDPOINT + `/owners/${autoOwnerId}`;
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: jwtToken,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(JSON.stringify(data));
      // 以下の部分は取得したデータの形状に基づいて修正が必要です
      setId(data.ownerId);
      setPassword(data.password);
      setSubscribeNumber(data.subscribeNumber);
      setOwnerName(data.ownerName);
      setDepartmentName(data.departmentName);
      setRepresentativeName(data.representativeName);
      setInChargeName(data.picName);
      setPhoneNumber(data.ownerPhone);
      setEmail(data.ownerEmail);
      setMemo(data.memo);
      setIsEnabled(data.isEnabled ? "有効" : "無効");
    } catch (error) {
      console.error("Owner Fetch Error:", error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [autoOwnerId]);

  const handlePasswordUpdate = async () => {
    // ユーザーがパスワードの更新を確認する
    setPasswordUpdateConfirm(true);
  };

  // 更新API
  const handleFormSubmit = async (event: React.FormEvent) => {
    setIsSubmitting(true);
    event.preventDefault();

    if (!subscribeNumber || !isEnabled) {
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        subscribeNumber: !subscribeNumber
          ? `契約者番号は必須です`
          : prevErrors.subscribeNumber,
        isEnabled: !isEnabled ? "状態を選択してください" : prevErrors.isEnabled,
      }));
      return; // エラーがあるため、保存操作を中断
    }

    // エラーがある場合、保存を阻止
    if (Object.values(validationErrors).some((error) => error !== "")) {
      setSnackbarMessage("入力エラーがあります。修正してください。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // JWTトークンを取得します
    const jwtToken = await getJwtTokenFromIdToken();
    console.log({
      id,
      password,
      subscribeNumber,
      ownerName,
      representativeName,
      inChargeName,
      phoneNumber,
      email,
      memo,
      isEnabled,
    });

    const url = process.env.REACT_APP_API_ENDPOINT + `/owners/${autoOwnerId}`; // change 'id' to appropriate id
    const status = isEnabled === "有効" ? true : false;

    // call updateOwner API
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: jwtToken,
      },
      body: JSON.stringify({
        subscribeNumber,
        ownerName,
        departmentName,
        representativeName,
        picName: inChargeName,
        ownerPhone: phoneNumber,
        ownerEmail: email,
        memo,
        isEnabled: status,
      }),
    });

    if (response.ok) {
      console.log("更新が完了しました。");
      setSnackbarMessage("更新が完了しました。");
      setAlertSeverity("success");
      setSnackbarOpen(true);
    } else if (response.status === 409) {
      console.error("契約者番号が重複しています。");
      setSnackbarMessage("契約者番号が重複しています。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
    } else {
      console.error("更新に失敗しました。");
      setSnackbarMessage("更新に失敗しました。");
      setAlertSeverity("error");
      setSnackbarOpen(true);
    }
    setIsSubmitting(false);
  };

  // 削除API
  const handleDelete = () => {
    // ユーザーが削除を確認する
    setOwnerDeleteConfirm(true);
  };

  const navigate = useNavigate();

  const handleCancelClick = () => {
    console.log(`戻るがクリックされました`);
    navigate(-1);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (hasError) {
    return <ErrorWithRetry onRetry={fetchData} />;
  }

  return (
    <CustomContainer maxWidth={false}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <PageTitle title={"LAD契約者（オーナー）詳細・編集"} />
        </Grid>
        <Grid item xs={12}>
          <form onSubmit={handleFormSubmit}>
            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>
                      <Typography>{id}</Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>パスワード</TableCell>
                    <TableCell>
                      <Box display={"flex"} alignItems={"center"}>
                        <Typography marginRight={2}>{password}</Typography>
                        <IconButton edge="end" onClick={handlePasswordUpdate}>
                          <RefreshIcon />
                          <Typography variant="body2">
                            パスワード更新
                          </Typography>
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      契約者番号
                      <RequiredLabel />
                    </TableCell>
                    <TableCell colSpan={3}>
                      <TextField
                        label="契約者番号"
                        value={subscribeNumber}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setSubscribeNumber(e.target.value)}
                        onBlur={() => handleBlur("subscribeNumber")}
                        helperText={validationErrors.subscribeNumber}
                        error={validationErrors.subscribeNumber !== ""}
                        inputProps={{
                          maxLength: CHARACTER_LIMIT_SUBSCRIBE_NUMBER,
                        }}
                      />
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell>名称(氏名)</TableCell>
                    <TableCell colSpan={3}>
                      <TextField
                        label="名称・氏名"
                        value={ownerName}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setOwnerName(e.target.value)}
                        onBlur={() => handleBlur("ownerName")}
                        helperText={validationErrors.ownerName}
                        error={validationErrors.ownerName !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>会社名(部署名)</TableCell>
                    <TableCell colSpan={3}>
                      <TextField
                        label="会社名(部署名)"
                        value={departmentName}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setDepartmentName(e.target.value)}
                        onBlur={() => handleBlur("departmentName")}
                        helperText={validationErrors.departmentName}
                        error={validationErrors.departmentName !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>代表者名</TableCell>
                    <TableCell colSpan={3}>
                      <TextField
                        label="代表者名"
                        value={representativeName}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setRepresentativeName(e.target.value)}
                        onBlur={() => handleBlur("representativeName")}
                        helperText={validationErrors.representativeName}
                        error={validationErrors.representativeName !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>担当者名</TableCell>
                    <TableCell colSpan={3}>
                      <TextField
                        label="担当者名"
                        value={inChargeName}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setInChargeName(e.target.value)}
                        onBlur={() => handleBlur("inChargeName")}
                        helperText={validationErrors.inChargeName}
                        error={validationErrors.inChargeName !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>連絡先(TEL)</TableCell>
                    <TableCell colSpan={3}>
                      <TextField
                        label="連絡先(TEL)"
                        value={phoneNumber}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        onBlur={() => handleBlur("phoneNumber")}
                        helperText={validationErrors.phoneNumber}
                        error={validationErrors.phoneNumber !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>連絡先(mail)</TableCell>
                    <TableCell colSpan={3}>
                      <TextField
                        label="連絡先(mail)"
                        value={email}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={() => handleBlur("email")}
                        helperText={validationErrors.email}
                        error={validationErrors.email !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>メモ</TableCell>
                    <TableCell colSpan={3}>
                      <TextField
                        label="メモ"
                        multiline
                        rows={3}
                        value={memo}
                        size="small"
                        sx={{ width: "600px" }}
                        onChange={(e) => setMemo(e.target.value)}
                        onBlur={() => handleBlur("memo")}
                        helperText={validationErrors.memo}
                        error={validationErrors.memo !== ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>状態</TableCell>
                    <TableCell colSpan={3}>
                      <RadioGroup
                        row
                        value={isEnabled}
                        onChange={(e) => setIsEnabled(e.target.value)}
                      >
                        <FormControlLabel
                          value="有効"
                          control={<Radio />}
                          label="有効"
                        />
                        <FormControlLabel
                          value="無効"
                          control={<Radio />}
                          label="無効"
                        />
                      </RadioGroup>
                      {validationErrors.isEnabled && (
                        <Typography color="error" variant="body2">
                          {validationErrors.isEnabled}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Box display={"flex"} justifyContent={"flex-end"} marginTop={2}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCancelClick}
              >
                戻る
              </Button>
              <Button
                type="button"
                variant="outlined"
                color="error"
                onClick={handleDelete}
                disabled={isDeleting}
                sx={{ marginLeft: 1 }}
              >
                {isDeleting ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    削除中...
                  </Box>
                ) : (
                  "削除"
                )}
              </Button>
              <Button
                type="submit"
                variant="outlined"
                color="primary"
                disabled={isSubmitting}
                sx={{ marginLeft: 1 }}
              >
                {isSubmitting ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    更新中...
                  </Box>
                ) : (
                  "更新"
                )}
              </Button>
            </Box>
          </form>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={alertSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={passwordUpdateConfirm}
        onClose={() => {
          setPasswordUpdateConfirm(false);
        }}
        onConfirm={async () => {
          const jwtToken = await getJwtTokenFromIdToken();
          const url =
            process.env.REACT_APP_API_ENDPOINT +
            `/owners/${autoOwnerId}/update-password`;
          // call updateOwnerPassword API
          const response = await fetch(url, {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: jwtToken,
            },
          });
          const data = await response.json();
          setPassword(data.password);
          if (response.ok) {
            setSnackbarMessage("パスワードの更新に成功しました。");
            setAlertSeverity("success");
            setSnackbarOpen(true);
          } else {
            console.error("パスワードの更新に失敗しました。");
            setSnackbarMessage("パスワードの更新に失敗しました。");
            setAlertSeverity("error");
            setSnackbarOpen(true);
          }
        }}
        title="確認"
        description="パスワードを更新しますか？"
      />

      <ConfirmDialog
        open={ownerDeleteConfirm}
        onClose={() => {
          setOwnerDeleteConfirm(false);
        }}
        onConfirm={async () => {
          setIsDeleting(true);
          const jwtToken = await getJwtTokenFromIdToken();
          const url =
            process.env.REACT_APP_API_ENDPOINT + `/owners/${autoOwnerId}`;
          // call deleteOwner API
          const response = await fetch(url, {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: jwtToken,
            },
          });

          const data = await response.json();

          // 失敗した場合
          if (!response.ok) {
            if (data.error === "the owner owned some buildings") {
              setSnackbarMessage(
                "削除に失敗しました。以下のビルが紐づいています。物件システムID: " +
                  data.ownedBuildingIds.join(", ")
              );
              setAlertSeverity("error");
              setSnackbarOpen(true);
            } else {
              console.error("削除に失敗しました。");
              setSnackbarMessage("削除に失敗しました。");
              setAlertSeverity("error");
              setSnackbarOpen(true);
            }
          } else {
            // 成功した場合
            console.log("削除成功");
            setOwnerDeletedConfirm(true);
          }
          setIsDeleting(false);
        }}
        title="確認"
        description="本当に削除しますか？"
      />

      <ConfirmDialog
        open={ownerDeletedConfirm}
        onClose={() => {
          setOwnerDeletedConfirm(false);
        }}
        onConfirm={() => {
          navigate(`/owners`); // 削除が成功したらLAD契約者（オーナー）一覧ページに戻る
        }}
        title="確認"
        description="削除が完了しました。"
        showCancelButton={false}
      />
    </CustomContainer>
  );
};

export default OwnerEditPage;
